/**
 * CS-3b — the Spec Book itself: the pages, drawn once.
 *
 * The builder hands it rows it built from the clips it already loaded; the
 * public page hands it rows the server built from the saved columns. Both draw
 * the same pages from the same code, so what a client opens is what the firm
 * saw when they saved it.
 */
import { useState } from 'react';

const PAGE_ROWS = 12;
const UNASSIGNED = 'Unassigned';
const UNCATEGORIZED = 'Uncategorized';

const STATE = {
  approved: { bucket: 'approved', label: 'Approved' },
  rejected: { bucket: 'declined', label: 'Declined' },
  submitted: { bucket: 'pending', label: 'Pending (sent)' },
};
const PENDING = { bucket: 'pending', label: 'Pending' };
const CHIP = { approved: 'chip ok', pending: 'chip wait', declined: 'chip' };

export const clipState = (clip) =>
  STATE[String(clip.client_approval_status || '').trim()] || PENDING;

// A stored price is a string a person typed: "$949.99", "1,250", "TBD".
export const clipPrice = (clip) => {
  const raw = String(clip.price ?? '').replace(/[^0-9.]/g, '');
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const money = (value) => `$${Math.round(value).toLocaleString()}`;

/**
 * One clip as one row, carrying only what the chosen columns allow — the same
 * whitelist the public route writes, so the two payloads cannot drift.
 */
export const rowFromClip = (clip, columns, dimensionsOf) => {
  const state = clipState(clip);
  const row = {
    id: clip.id,
    title: clip.title || 'Untitled',
    manufacturer: clip.manufacturer || '',
    room_id: clip.room_id || null,
    room: clip.room || '',
    spec_category: clip.spec_category || '',
    state: state.bucket,
    state_label: state.label,
  };
  if (columns.where) row.room_location = clip.room_location || '';
  if (columns.dims) row.dimensions = dimensionsOf(clip);
  if (columns.price) row.price = clipPrice(clip);
  if (columns.lead) row.lead_time = clip.lead_time || '';
  if (columns.note) row.notes = clip.notes || '';
  if (columns.image) row.image = clip.primary_image || (clip.images || [])[0] || null;
  return row;
};

/**
 * Every row lands in exactly one group, and the groups come out in the
 * project's own order: the rooms list first, then names nobody put on the list,
 * then the rows with no room at all.
 */
export const groupRows = (rows, rooms, organise) => {
  const byId = new Map((rooms || []).filter((r) => r.id).map((r) => [r.id, r]));
  const bucket = new Map();
  const add = (key, name, row) => {
    if (!bucket.has(key)) bucket.set(key, { key, name, rows: [] });
    bucket.get(key).rows.push(row);
  };
  (rows || []).forEach((row) => {
    if (organise === 'name') { add('all', 'All selections', row); return; }
    if (organise === 'category') {
      const category = String(row.spec_category || '').trim();
      add(category || UNCATEGORIZED, category || UNCATEGORIZED, row);
      return;
    }
    const room = row.room_id ? byId.get(row.room_id) : null;
    if (room) { add(room.id, room.name, row); return; }
    const text = String(row.room || '').trim();
    add(text || UNASSIGNED, text || UNASSIGNED, row);
  });

  const rank = new Map((rooms || []).filter((r) => r.id).map((r, i) => [r.id, i]));
  const order = (group) => {
    if (group.name === UNASSIGNED || group.name === UNCATEGORIZED) return 2;
    return byId.has(group.key) ? 0 : 1;
  };
  const list = [...bucket.values()];
  list.sort((a, b) => {
    if (order(a) !== order(b)) return order(a) - order(b);
    if (order(a) === 0) return rank.get(a.key) - rank.get(b.key);
    return a.name.localeCompare(b.name);
  });
  list.forEach((group) => group.rows.sort((a, b) =>
    String(a.title || '').localeCompare(String(b.title || ''))));
  return list;
};

export const bookGroups = (rows, rooms, options) => {
  const picked = options.rooms || [];
  const statuses = options.statuses || [];
  const kept = (rows || []).filter((row) => !statuses.length || statuses.includes(row.state));
  return groupRows(kept, rooms, options.organise || 'room')
    .filter((group) => !picked.length || picked.includes(group.key))
    .map((group) => ({
      ...group,
      sum: group.rows.reduce((total, row) => total + (row.price || 0), 0),
    }));
};

// 12 rows to a page; a group that runs past the bottom picks up its own name
// again at the top of the next one.
export const bookPages = (groups) => {
  const out = [];
  let page = [];
  let filled = 0;
  groups.forEach((group) => {
    let i = 0;
    while (i < group.rows.length) {
      if (filled >= PAGE_ROWS) { out.push(page); page = []; filled = 0; }
      const take = group.rows.slice(i, i + (PAGE_ROWS - filled));
      page.push({ ...group, page_rows: take });
      i += take.length;
      filled += take.length;
    }
  });
  if (page.length) out.push(page);
  return out;
};

/**
 * CS-3c — the client's answer, in the cell where the state already sits.
 * Approve and Decline act at once; Ask opens one line and sends it. A filled
 * button does nothing when clicked again: a client cannot un-answer.
 */
const AnswerCell = ({ row, onAnswer }) => {
  const [state, setState] = useState(row.state);
  const [label, setLabel] = useState(row.state_label);
  const [when, setWhen] = useState(row.answered_at || '');
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

  const send = async (status, notes) => {
    if (busy) return;
    if (status === 'approved' && state === 'approved') return;
    if (status === 'rejected' && state === 'declined') return;
    setBusy(true);
    const answer = await onAnswer(row.id, status, notes);
    if (answer) {
      setState(answer.state);
      setLabel(answer.state_label);
      setWhen(answer.at);
      if (status === 'question') { setAsked(true); setAsking(false); setNote(''); }
    }
    setBusy(false);
  };

  return (
    <div className="ksh-ans" data-testid={`ksh-answer-${row.id}`}>
      <span className={CHIP[state] || 'chip'} data-testid={`ksh-state-${row.id}`}>{label}</span>
      {when ? <span className="when" data-testid={`ksh-answered-${row.id}`}>
        {asked ? `Asked · ${day(when)}` : `· ${day(when)}`}
      </span> : null}
      <button type="button" data-km-noprint className={state === 'approved' ? 'on-ok' : ''} disabled={busy}
        onClick={() => send('approved')} data-testid={`ksh-approve-${row.id}`}>Approve</button>
      <button type="button" data-km-noprint className={state === 'declined' ? 'on-no' : ''} disabled={busy}
        onClick={() => send('rejected')} data-testid={`ksh-decline-${row.id}`}>Decline</button>
      {asking ? (
        <>
          <input value={note} autoFocus data-km-noprint placeholder="What would you like to know?"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send('question', note); }}
            data-testid={`ksh-ask-field-${row.id}`} />
          <button type="button" data-km-noprint disabled={busy} onClick={() => send('question', note)}
            data-testid={`ksh-ask-send-${row.id}`}>Send</button>
        </>
      ) : (
        <button type="button" data-km-noprint onClick={() => setAsking(true)}
          data-testid={`ksh-ask-${row.id}`}>Ask</button>
      )}
    </div>
  );
};

export const ScheduleBook = ({ firm, project, schedule, rooms, rows, imageBase = '', onAnswer = null }) => {
  const options = schedule.options || {};
  const columns = options.columns || {};
  const groups = bookGroups(rows, rooms, options);
  const pages = bookPages(groups);
  const firmName = firm?.name || '';
  const projectName = project?.name || '';
  const clientName = project?.client_name || '';
  const today = new Date().toLocaleDateString();

  const head = [
    columns.image && { id: 'image', name: '' },
    { id: 'item', name: 'Item' },
    columns.dims && { id: 'dims', name: 'Dimensions' },
    columns.price && { id: 'price', name: 'Price', n: true },
    columns.lead && { id: 'lead', name: 'Lead time' },
    columns.note && { id: 'note', name: 'Trade note' },
    columns.approval && { id: 'approval', name: 'State' },
  ].filter(Boolean);

  const meta = (
    <>
      {projectName}<br />
      {clientName ? `Prepared for ${clientName}` : null}{clientName ? <br /> : null}
      {today}
    </>
  );

  const line = (row) => {
    const where = [row.manufacturer,
      columns.where && (options.organise || 'room') !== 'room' && row.room,
      columns.where && row.room_location].filter(Boolean).join(' · ');
    return (
      <tr key={row.id} data-testid={`ksh-row-${row.id}`}>
        {columns.image ? (
          <td>
            {row.image ? <img className="img" src={`${imageBase}${row.image}`} alt="" />
              : <span className="img" />}
          </td>
        ) : null}
        <td>
          <div className="t">{row.title}</div>
          {where ? <div className="m">{where}</div> : null}
        </td>
        {columns.dims ? <td>{row.dimensions || '—'}</td> : null}
        {columns.price ? <td className="n">{row.price ? money(row.price) : '—'}</td> : null}
        {columns.lead ? <td>{row.lead_time || '—'}</td> : null}
        {columns.note ? <td>{row.notes || '—'}</td> : null}
        {columns.approval ? (
          <td>
            {onAnswer && schedule.allow_answers !== false
              ? <AnswerCell row={row} onAnswer={onAnswer} />
              : <span className={CHIP[row.state] || 'chip'}
                data-testid={`ksh-state-${row.id}`}>{row.state_label}</span>}
          </td>
        ) : null}
      </tr>
    );
  };

  if (!pages.length) {
    return (
      <div className="ksh-pg" data-testid="schedule-none-match">
        {rows && rows.length ? 'Nothing matches these choices.'
          : 'No selections yet. Clip materials to this project first.'}
      </div>
    );
  }

  return (
    <>
      {options.cover ? (
        <div className="ksh-cover" data-testid="schedule-cover-page">
          <div className="firm">
            {firm?.mark ? <img src={firm.mark} alt={firmName} className="h-10 mb-2" /> : firmName}
          </div>
          <div className="t">Spec Book</div>
          <div className="m">{meta}</div>
        </div>
      ) : null}

      {pages.map((page, index) => (
        <div className="ksh-pg" key={index} data-testid={`schedule-page-${index + 1}`}>
          <div className="ksh-pg__hd">
            <div>
              <div className="firm">{firmName}</div>
              <div className="t">Spec Book</div>
            </div>
            <div className="meta">{meta}</div>
          </div>
          <div>
            {page.map((group, gi) => (
              <div key={`${group.key}-${gi}`}>
                <div className="ksh-pg__room" data-testid={`schedule-group-${group.key}`}>
                  <span>{group.name}</span>
                  <span className="n">
                    {group.rows.length} {group.rows.length === 1 ? 'selection' : 'selections'}
                    {columns.price && group.sum ? ` · selected ${money(group.sum)}` : ''}
                  </span>
                </div>
                <table>
                  <thead>
                    <tr>
                      {head.map((h) => (
                        <th key={h.id} className={h.n ? 'n' : ''}
                          style={h.id === 'image' ? { width: 44 } : undefined}>{h.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{group.page_rows.map(line)}</tbody>
                </table>
              </div>
            ))}
          </div>
          <div className="ksh-pg__ft">
            <span>{[firmName, projectName, schedule.name || 'Spec Book'].filter(Boolean).join(' · ')}</span>
            <span>page {index + 1} / {pages.length}</span>
          </div>
        </div>
      ))}
    </>
  );
};

export default ScheduleBook;
